import { useState, useContext, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faTrash as solidStar } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import "../styles/SearchResult.css";
import { APIBaseLink } from "../components/APIBaseLink";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const SavedSearches = ({ result, index }) => {
	const { user } = useContext(AuthContext); // Access user
	const [resources, setResources] = useState([]); // Store resources (saved searches)
	const [page, setPage] = useState(1); // Store current page
	const [totalPages, setTotalPages] = useState(1); // Store total number of pages
	const [loading, setLoading] = useState(true); // Loading state to show spinner
	const navigate = useNavigate();
	const { zipCode, updateZipCode } = useContext(AuthContext);
	const { countyName, updateCountyName } = useContext(AuthContext); //gets local storage of countyName

	const API_BASE_LINK = APIBaseLink();


	useEffect(() => {
		if (user?.id) {
			setLoading(true); // Set loading state before making the API call
			const token = localStorage.getItem("token");

			fetch(`${API_BASE_LINK}/user/getSavedSearches?userId=${user.id}&page=${page}&limit=10`, {
				method: "GET",
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
			})
				.then((response) => response.json())
				.then((data) => {
					if (data?.searches) {
						setTotalPages(Math.ceil(data.total / 10)); // Calculate total pages

						let formattedSearches = [];
						for (let i = 0; i < data.searches.length; i++) {
							let formattedSearch = "";
							const tokens = data.searches[i].split("&"); // Separates Parameters
							for (let j = 0; j < tokens.length; j++) {
								const [key, value] = tokens[j].split("="); // Separates Key and Value

								// Formats each Key Value pair to the string
								switch (key) {
									case "keyword":
										formattedSearch += `Category: ${value}`;
										break;
									case "zipCode":
										formattedSearch += `Zip Code: ${value}`;
										break;
									case "searchTerm":
										formattedSearch += `Search: ${value}`;
										break;
									case "countyName":
										formattedSearch += `County: ${value}`;
										break;
									default:
										break;
								}

								if (j < tokens.length - 1) {
									formattedSearch += ", ";
								}
							}

							// Cleans up Spaces and Special Characteres
							formattedSearch = formattedSearch.replaceAll("+", " ");
							formattedSearch = decodeURIComponent(formattedSearch);
							formattedSearches.push({
								query: data.searches[i],
								formatted: formattedSearch,
							}); // Adds search to final array
						}
						setResources(formattedSearches);
					} else {
						setResources([]); // If no resources, set as empty array
					}
					setLoading(false); // Set loading to false when data is fetched
				})
				.catch((error) => {
					console.error("Error fetching saved searches:", error);
					setResources([]); // If error, clear the resources
					setLoading(false); // Set loading to false on error
				});
		}
	}, [page, user?.id]); // Fetch data when page or user.id changes

	// Helper function to check if a field is valid (not "N/A", "NA", or empty string)
	const isValidField = (field) => {
		return typeof field === "string" && field.toLowerCase() !== "n/a" && field.toLowerCase() !== "na" && field.trim() !== "";
	};

	// Function to handle unfavoriting
	const handleDeleteSearch = (query) => {
		if (window.confirm("Are you sure you want to delete this search?")) {
			try {
				const updatedResources = resources.filter((thisResource) => thisResource.query !== query); // Remove the unfavorited resource
				setResources(updatedResources);

				const token = localStorage.getItem("token");

				// Call API to update backend
				fetch(`${API_BASE_LINK}/user/removeSavedSearch`, {
					method: "POST",
					headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
					body: JSON.stringify({ userId: user.id, search: query }),
				}).catch((error) => {
					console.error("Error removing search:", error);
					toast.error("Error Deleting Search.");
				});
				toast.success("Search Deleted.");
			} catch (error) {
				console.error("Error: ", error);
				toast.error("Error Deleting Search");
			}
		}
	};

	// Uses given search string to search database
	const handleSearch = (resource) => {
		try {
			const tokens = resource.split("&"); // Separates Parameters
			for (let j = 0; j < tokens.length; j++) {
				const [key, value] = tokens[j].split("="); // Separates Key and Value

				// Formats each Key Value pair to the string
				switch (key) {
					case "zipCode":
						updateZipCode(value);
						updateCountyName("");
						break;
					case "countyName":
						updateCountyName(value);
						updateZipCode("");
						break;
					default:
						break;
				}
			}
			toast.success("Search Restored.");
			navigate(`/search-results?${resource}`);
		} catch (error) {
			toast.error("Problem Restoring Search.")
		}
	};

	return (
		<div>
			<div className="search-header">
				<div className="back-button-container">
					<button className="back-button" onClick={() => navigate("/")}>
						<FontAwesomeIcon icon={faHome} style={{ color: "white" }} />
					</button>
				</div>
				<h1 style={{ textAlign: "center" }}>Saved Searches</h1>
			</div>
			{loading ? (
				<p>Loading...</p> // Show loading message until data is fetched
			) : (
				<>
					{resources.length > 0 ? (
						<div className="entries">
							{/* Make String 2 parts:
								Part 1: Plain Text String - This is what is displayed on the page
								Part 2: Search Query - This is what is passed to the search result url
							 */}
							{resources.map((resource, index) => (
								<div key={index} style={{ width: "70%", marginLeft: "auto", marginRight: "auto" }} className="search-result">
									<h2>{resource.formatted}</h2>

									<div className="action-container">
										{/* Quick Search Button */}
										<button className="edit" onClick={() => handleSearch(resource.query)}>
											Quick Search
										</button>

										{/* Remove Search Button */}
										<button className="delete" onClick={() => handleDeleteSearch(resource.query)}>
											Remove Search
										</button>
									</div>
								</div>
							))}
						</div>
					) : (
						<p>No saved searches yet.</p>
					)}

					<div className="pagination">
						<button className="navButtons" onClick={() => setPage((prev) => Math.max(prev - 1, 1))} disabled={page === 1}>
							Previous
						</button>
						<span>
							Page {page} of {totalPages}
						</span>
						<button className="navButtons" onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))} disabled={page === totalPages}>
							Next
						</button>
					</div>
				</>
			)}
		</div>
	);
};

export default SavedSearches;
